import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import PDFPage from './PDFDocument';

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const styles = StyleSheet.create({
  pageTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#4F5F4B',
    marginBottom: 20,
  },
  mainContainer: {
    flexDirection: 'row',
    marginBottom: 15,
  },
  column: {
    flex: 1,
    marginHorizontal: 5,
  },
  sectionBox: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    border: '1px solid #e0e0e0',
  },
  sectionHeader: {
    backgroundColor: '#4F5F4B',
    color: 'white',
    padding: 10,
    textAlign: 'center',
    borderRadius: 6,
    marginBottom: 10,
    fontSize: 14,
    fontWeight: 'bold',
  },
  row: {
    flexDirection: 'row',
    marginVertical: 3,
    paddingVertical: 2,
  },
  label: {
    fontSize: 10,
    flex: 1,
    color: '#333',
  },
  value: {
    fontSize: 10,
    textAlign: 'right',
    minWidth: 80,
    color: '#333',
  },
  boldLabel: {
    fontSize: 10,
    flex: 1,
    fontWeight: 'bold',
    color: '#4F5F4B',
  },
  boldValue: {
    fontSize: 10,
    textAlign: 'right',
    minWidth: 80,
    fontWeight: 'bold',
    color: '#4F5F4B',
  },
  divider: {
    borderTopWidth: 1,
    borderTopColor: '#ccc',
    marginVertical: 5,
  },
  thickDivider: {
    borderTopWidth: 2,
    borderTopColor: '#4F5F4B',
    marginVertical: 8,
  },
  subsectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 4,
    color: '#4F5F4B',
  },
  chartContainer: {
    marginTop: 10,
    alignItems: 'center',
  },
  chartImage: {
    width: '100%',
    maxWidth: 500,
    height: 'auto',
  },
  chartDescription: {
    fontSize: 9,
    color: '#666',
    marginTop: 10,
    padding: 10,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
  },
  chartDescriptionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  chartDescriptionText: {
    fontSize: 8,
    marginBottom: 2,
    lineHeight: 1.3,
  },
  riskBadge: {
    backgroundColor: '#dc3545',
    color: 'white',
    fontSize: 8,
    padding: 4,
    borderRadius: 3,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  infoText: {
    fontSize: 9,
    color: '#666',
    fontStyle: 'italic',
  },
});

const DefaultPlanPDF = ({ clientData, chartImages = {}, pageNumber }) => {
  const chartImage = chartImages['default-plan-chart'];

  // Calculate values similar to the Default Plan page
  const totalNQAssets = clientData.assets?.filter((a) => a.type === 'NON_QUALIFIED')
    .reduce((acc, a) => acc + (Number(a.value) || 0), 0) || 0;

  const currentInvestableAssets = clientData.include_nq_assets !== false
    ? clientData.totalRetAssets
    : clientData.totalRetAssets - totalNQAssets;

  const age = clientData.age || 0;
  const spouseAge = clientData.spouseAge || '';
  const retirementAge = clientData.retirement_age || 65;
  const yearsToRetirement = Math.max(0, retirementAge - age);
  const retirementYear = clientData.dob
    ? new Date(clientData.dob).getFullYear() + retirementAge
    : 'N/A';

  const inflationRate = (Number(clientData.inflation_rate) || 2.5) / 100;
  const stockAllocation = (Number(clientData.stock_allocation) || 60) / 100;
  const bondAllocation = (Number(clientData.bond_allocation) || 40) / 100;
  // Compliance-locked, non-editable assumption rates (Summit Compliance Group).
  // Not read from the client record so a stale saved value cannot override the lock.
  const equityGrowthRate = 7.5 / 100;
  const bondGrowthRate = 2.2 / 100;
  const assetWithdrawalRate = (Number(clientData.asset_withdrawal_rate) || 4) / 100;
  const effectiveTaxRate = (Number(clientData.effective_tax_rate) || 0) / 100;

  // Projected assets at retirement
  const projectedStockAssets = currentInvestableAssets * stockAllocation * Math.pow(1 + equityGrowthRate, yearsToRetirement);
  const projectedBondAssets = currentInvestableAssets * bondAllocation * Math.pow(1 + bondGrowthRate, yearsToRetirement);
  const projectedRetirementAssets = projectedStockAssets + projectedBondAssets;

  // Projected income at retirement
  const inflationMultiplier = Math.pow(1 + inflationRate, yearsToRetirement);
  const projectedATINeeded = (Number(clientData.desired_ati) || 0) * inflationMultiplier;
  const projectedSSI = ((Number(clientData.social_security) || 0) + (Number(clientData.spouse_social_security) || 0)) * inflationMultiplier;
  const projectedPension = ((Number(clientData.pension) || 0) + (Number(clientData.spouse_pension) || 0)) * inflationMultiplier;
  const projectedRental = ((Number(clientData.rental) || 0) + (Number(clientData.spouse_rental) || 0)) * inflationMultiplier;
  const assetWithdrawals = projectedRetirementAssets * assetWithdrawalRate;

  const totalProjectedIncome = projectedSSI + projectedPension + projectedRental + assetWithdrawals;
  const totalProjectedATI = totalProjectedIncome * (1 - effectiveTaxRate);
  const projectedIncomeGap = totalProjectedATI - projectedATINeeded;

  const needsRiskManagement = yearsToRetirement <= 10;

  return (
    <PDFPage
      header="Default Retirement Plan"
      pageNumber={pageNumber}
      showDisclaimer={true}
      showFooter={true}
    >
      <View style={styles.mainContainer}>
        {/* Projected Asset Growth Column */}
        <View style={styles.column}>
          <View style={styles.sectionBox}>
            <Text style={styles.sectionHeader}>Projected Asset Growth</Text>

            <View style={styles.row}>
              <Text style={styles.label}>Current Investable Assets:</Text>
              <Text style={styles.value}>{formatCurrency(currentInvestableAssets)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Retirement Year:</Text>
              <Text style={styles.value}>{retirementYear}</Text>
            </View>

            {needsRiskManagement && (
              <View style={styles.row}>
                <Text style={styles.label}></Text>
                <Text style={styles.riskBadge}>Risk Mgmt Needed</Text>
              </View>
            )}

            <View style={styles.row}>
              <Text style={styles.label}>Age(s) in Year of Retirement:</Text>
              <Text style={styles.value}>
                {retirementAge}{spouseAge ? ` / ${spouseAge + yearsToRetirement}` : ''}
              </Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.row}>
              <Text style={styles.label}>Stock Allocation:</Text>
              <Text style={styles.value}>
                {formatCurrency(currentInvestableAssets * stockAllocation)} ({(stockAllocation * 100).toFixed(0)}%)
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Bond Allocation:</Text>
              <Text style={styles.value}>
                {formatCurrency(currentInvestableAssets * bondAllocation)} ({(bondAllocation * 100).toFixed(0)}%)
              </Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.row}>
              <Text style={styles.boldLabel}>Projected Stock Assets:</Text>
              <Text style={styles.boldValue}>{formatCurrency(projectedStockAssets)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}></Text>
              <Text style={styles.infoText}>
                {yearsToRetirement} Yrs @ {(equityGrowthRate * 100).toFixed(1)}%
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.boldLabel}>Projected Bond Assets:</Text>
              <Text style={styles.boldValue}>{formatCurrency(projectedBondAssets)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}></Text>
              <Text style={styles.infoText}>
                {yearsToRetirement} Yrs @ {(bondGrowthRate * 100).toFixed(1)}%
              </Text>
            </View>

            <View style={styles.thickDivider} />

            <View style={styles.row}>
              <Text style={styles.boldLabel}>Projected Retirement Assets:</Text>
              <Text style={styles.boldValue}>{formatCurrency(projectedRetirementAssets)}</Text>
            </View>
          </View>
        </View>

        {/* Projected Income Column */}
        <View style={styles.column}>
          <View style={styles.sectionBox}>
            <Text style={styles.sectionHeader}>Projected Income</Text>

            <View style={styles.row}>
              <Text style={styles.label}>Projected Annual Target Income (ATI) Needed ({retirementYear}):</Text>
              <Text style={styles.value}>{formatCurrency(projectedATINeeded)}</Text>
            </View>

            <Text style={styles.subsectionTitle}>Income Sources:</Text>

            <View style={styles.row}>
              <Text style={styles.label}>SSI:</Text>
              <Text style={styles.value}>{formatCurrency(projectedSSI)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Pension:</Text>
              <Text style={styles.value}>{formatCurrency(projectedPension)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Rental:</Text>
              <Text style={styles.value}>{formatCurrency(projectedRental)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Asset Withdrawals ({(assetWithdrawalRate * 100).toFixed(0)}%):
              </Text>
              <Text style={styles.value}>{formatCurrency(assetWithdrawals)}</Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.row}>
              <Text style={styles.boldLabel}>Total Projected Income:</Text>
              <Text style={styles.boldValue}>{formatCurrency(totalProjectedIncome)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.boldLabel}>Total Projected Annual Target Income (ATI):</Text>
              <Text style={styles.boldValue}>{formatCurrency(totalProjectedATI)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.boldLabel}>Projected Income Gap:</Text>
              <Text style={styles.boldValue}>{formatCurrency(projectedIncomeGap)}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.boldLabel}>% Assets Used for Income:</Text>
              <Text style={styles.boldValue}>100%</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Chart Section */}
      <View style={styles.chartContainer}>
        {chartImage ? (
          <>
            <Image src={chartImage} style={styles.chartImage} />
            <View style={styles.chartDescription}>
              <Text style={styles.chartDescriptionTitle}>Sequence of Returns Risk Illustration:</Text>
              <Text style={styles.chartDescriptionText}>• Same starting balance</Text>
              <Text style={styles.chartDescriptionText}>• Same withdrawal amounts</Text>
              <Text style={styles.chartDescriptionText}>• Same average annual returns (6%)</Text>
              <Text style={styles.chartDescriptionText}>• Scenario 1: Repeating returns of 25%, 10%, 5%, -15%</Text>
              <Text style={styles.chartDescriptionText}>• Scenario 2: Repeating returns of -15%, 5%, 10%, 25%</Text>
              <Text style={styles.chartDescriptionText}>• Negative returns early in retirement can have a massive impact on the chance of outliving savings.</Text>
              <Text style={[styles.chartDescriptionText, { fontWeight: 'bold' }]}>• Are you comfortable leaving your retirement strategy up to chance?</Text>
            </View>
          </>
        ) : (
          <Text style={styles.infoText}>(Sequence of Returns Risk chart not available)</Text>
        )}
      </View>
    </PDFPage>
  );
};

export default DefaultPlanPDF;
